import type { SeatSourceType } from '@package/drizzle/schema'

/**
 * Every seat flavour: how it is named, and whether the recipient paid for it.
 *
 * One table rather than a label map beside a list of grant types — those two drifting apart is the
 * whole reason a directly provisioned pool ever read "Beasiswa". Typed as a total `Record` so a new
 * `SeatSourceTypeEnum` value fails to compile here instead of silently defaulting.
 *
 * Only `sponsored_scholarship` is a beasiswa. The grant family also covers a free trial, a pilot, a
 * seat PiBo provisioned directly, and a pemda programme; calling those "Beasiswa" claims a school
 * received a scholarship it never got, and the word carries reporting weight.
 */
const SEAT_SOURCE: Record<SeatSourceType, { label: string; isGrant: boolean }> = {
  sponsored_scholarship: { label: 'Beasiswa', isGrant: true },
  free_trial: { label: 'Uji coba', isGrant: true },
  pilot: { label: 'Program pilot', isGrant: true },
  internal: { label: 'Penyediaan langsung', isGrant: true },
  pemda: { label: 'Program pemda', isGrant: true },
  // Revenue family: offline-paid, so it is a purchase and reads like one.
  direct_paid: { label: 'Kursi dibeli', isGrant: false },
}

/** True when nobody paid for the seat — the grant family, `direct_paid` excluded. */
export function isGrantSeatSource(sourceType: SeatSourceType): boolean {
  return SEAT_SOURCE[sourceType].isGrant
}

/**
 * Names one POOL's seats, for billing surfaces that describe what the organization holds.
 *
 * Not the same question as a roster row's provenance: this says what the pool is, while the Akses
 * column says who covers the child. A purchased pool is "Kursi dibeli" here and "Sekolah" there.
 *
 * `sourceType` is null for a plain purchase — no entitlement backs it — so `isCommercial` carries
 * the answer in that case.
 */
export function seatSourceLabel(sourceType: SeatSourceType | null, isCommercial: boolean): string {
  if (sourceType) return SEAT_SOURCE[sourceType].label

  return isCommercial ? 'Kursi dibeli' : 'Kursi bantuan'
}

/**
 * Title for a group of grant pools, which may mix flavours.
 *
 * One flavour names itself; a mix falls back to the neutral family word rather than picking a
 * winner, because every line underneath states its own flavour anyway.
 */
export function grantGroupLabel(sourceTypes: (SeatSourceType | null)[]): string {
  const [first, ...rest] = sourceTypes.map((sourceType) => seatSourceLabel(sourceType, false))
  if (first === undefined) return 'Kursi bantuan'

  return rest.every((label) => label === first) ? first : 'Kursi bantuan'
}
